import moment from 'moment';
import type { Granularity } from '../../../types/timeframe';
import { formatWeeklyLabelTooltip } from './formatWeeklyLabel';

/**
 * Parse a raw period token to the start-of-period moment. Handles both the
 * backend's compact tokens ("YYYY-MM", "YYYY-Qn") and the full ISO dates
 * ("YYYY-MM-DD") used by the local mock-data periods.
 */
function periodStart(period: string, granularity: Granularity): moment.Moment {
  const quarterMatch = /^(\d{4})-Q(\d)$/.exec(period);
  if (quarterMatch) {
    return moment(quarterMatch[1], 'YYYY').quarter(parseInt(quarterMatch[2], 10)).startOf('quarter');
  }
  if (/^\d{4}-\d{2}$/.test(period)) {
    return moment(period, 'YYYY-MM').startOf('month');
  }
  const date = moment(period, 'YYYY-MM-DD');
  if (granularity === 'monthly') return date.startOf('month');
  if (granularity === 'quarterly') return date.startOf('quarter');
  return date;
}

/**
 * Builds the full (year-inclusive) date text shown in the chart tooltip for a
 * given raw period token, based on the active granularity:
 *  - daily: "Feb 5, 2026"
 *  - weekly: "14 Sep 26 to 20 Sep 26" (clamped to the selected reporting range)
 *  - monthly: "Aug 26"
 *  - quarterly: "Q1 2025"
 */
export function formatTooltipPeriod(
  period: string | undefined,
  granularity: Granularity | undefined,
  rangeStart?: string,
  rangeEnd?: string,
): string {
  if (!period || !granularity) return period || '';

  if (granularity === 'weekly') {
    return formatWeeklyLabelTooltip(period, rangeStart, rangeEnd);
  }

  const start = periodStart(period, granularity);
  if (!start.isValid()) return period;

  switch (granularity) {
    case 'daily':
      return start.format('MMM D, YYYY');
    case 'quarterly':
      return `Q${start.quarter()} ${start.format('YYYY')}`;
    case 'monthly':
      return start.format('MMM YY');
    default:
      return start.format('MMM D, YYYY');
  }
}
